import { List, Record } from 'immutable';
import BaseStore from './BaseStore';
import { ActionTypes } from '../constants/AppConstants';
import { resolveAll } from '../helpers/LinkMapper';
import Reservation from '../records/Reservation';
import { RESERVATION_CANCELLATION_STATUS } from '../constants/AppConstants';


const State = new Record({
    reservations: new List(),
    reservation: new Reservation(),
    isReservationLoading: false,
    isReservationFlowStarted: false,
    isReservationFlowCanceled: false,
    isReservationFlowCompleted: false,
    bikeReservation: null,
    seatReservation: null,
    reservationWidgetToggled: false,
    reservationCancellationStatus: null,
    totalPages: 0
});

const defaultState = new State();

class ReservationStore extends BaseStore {
    deserialize(serialized) {
        if (serialized) {
            return defaultState.merge({
                reservations: resolveAll(serialized.reservations).as(Reservation),
            });
        }

        return defaultState;
    }

    getTotalPages() {
        return this.state.get('totalPages');
    }

    getUpcomingReservations() {
        const reservations = this.state.get('reservations');
        const upcoming = reservations.filter(reservation => !reservation.isExpired && !reservation.isCancelled);

        if (upcoming.size > 0) {
            return upcoming;
        }
        return null;
    }

    getPreviousReservations() {
        const reservations = this.state.get('reservations');

        const previous = reservations.filter(reservation => reservation.isExpired || reservation.isCancelled);

        if (previous.size > 0) {
            return previous;
        }

        return null;
    }

    hasReservations() {
        const reservations = this.state.get('reservations');
        return reservations.size > 0;
    }

    getReservationsState() {
        return this.state.get('reservations');
    }

    getReservation() {
        return this.state.get('reservation');
    }

    getReservationCancellationStatus() {
        return this.state.get('reservationCancellationStatus');
    }

    handleClear() {
        this.setState(defaultState);
    }

    setLoading(isLoading) {
        return function() {
            this.setState(this.state.set('isReservationLoading', isLoading));
        };
    }

    setReservationWidgetToggled({ toggled }) {
        this.setState(this.state.set('reservationWidgetToggled', toggled));
    }

    getIsReservationWidgetToggled() {
        return this.state.get('reservationWidgetToggled');
    }

    setIsReservationStarted() {
        this.setState(this.state.set('isReservationFlowStarted', true));
    }

    setIsReservationCanceled() {
        this.setState(this.state.set('isReservationFlowCanceled', true));
    }

    setIsReservationCompleted() {
        this.setState(this.state.set('isReservationFlowCompleted', true));
    }

    addBikeReservation({ bikeReservation }) {
        this.setState(this.state.set('bikeReservation', bikeReservation));
    }

    removeBikeReservation() {
        this.setState(this.state.set('bikeReservation', defaultState.bikeReservation));
    }

    addSeatReservation({ seatReservation }) {
        this.setState(this.state.set('seatReservation', seatReservation));
    }

    removeSeatReservation() {
        this.setState(this.state.set('seatReservation', defaultState.seatReservation));
    }

    createReservation({ reservation }) {
        this.setState(this.state.set('reservation', reservation));
    }

    amendReservation({ reservation }) {
        this.setState(this.state.set('reservation', reservation));
    }

    getReservations({ pagination, reservations }) {
        this.setState(this.state.set('reservations', reservations));
        this.setState(this.state.set('totalPages', pagination.totalPages));
    }

    handleCancelReservationSuccess () {
        this.setState(this.state.set('reservationCancellationStatus', RESERVATION_CANCELLATION_STATUS.SUCCESS));
    }

    handleCancelReservationFailure () {
        this.setState(this.state.set('reservationCancellationStatus', RESERVATION_CANCELLATION_STATUS.FAILURE));
    }

    clearCancelReservationStatus () {
        this.setState(this.state.set('reservationCancellationStatus', null));
    }

    getActionHandlers() {
        return {
            [ActionTypes.START_RESERVATION_FLOW]: this.setIsReservationStarted,
            [ActionTypes.CANCEL_RESERVATION_FLOW]: this.setIsReservationCanceled,
            [ActionTypes.RESERVATION_COMPLETED]: this.setIsReservationCompleted,

            [ActionTypes.ADD_BIKE_RESERVATION]: this.addBikeReservation,
            [ActionTypes.REMOVE_BIKE_RESERVATION]: this.removeBikeReservation,
            [ActionTypes.ADD_SEAT_RESERVATION]: this.addSeatReservation,
            [ActionTypes.REMOVE_SEAT_RESERVATION]: this.removeSeatReservation,

            [ActionTypes.CREATE_RESERVATION_STARTED]: this.setLoading(true),
            [ActionTypes.CREATE_RESERVATION_LOADED]: this.createReservation,
            [ActionTypes.CREATE_RESERVATION_FINISHED]: this.setLoading(false),

            [ActionTypes.AMEND_RESERVATION_STARTED]: this.setLoading(true),
            [ActionTypes.AMEND_RESERVATION_LOADED]: this.amendReservation,
            [ActionTypes.AMEND_RESERVATION_FINISHED]: this.setLoading(false),

            [ActionTypes.GET_RESERVATION_STARTED]: this.setLoading(true),
            [ActionTypes.GET_RESERVATION_LOADED]: this.getReservations,
            [ActionTypes.GET_RESERVATION_FINISHED]: this.setLoading(false),

            [ActionTypes.RESERVATION_WIDGET_TOGGLED]: this.setReservationWidgetToggled,

            [ActionTypes.CANCEL_RESERVATION_SUCCESS]: this.handleCancelReservationSuccess,
            [ActionTypes.CANCEL_RESERVATION_FAILED]: this.handleCancelReservationFailure,
            [ActionTypes.CANCEL_RESERVATION_FAILED]: this.handleCancelReservationFailure,
            [ActionTypes.CLEAR_CANCEL_RESERVATION_STATUS]: this.clearCancelReservationStatus,

            [ActionTypes.CLEAR_RESERVATION_STORE]: this.handleClear(),

            [ActionTypes.SET_RESERVATION_PAGINATION]: ({ totalPages }) => {
                this.setState(this.state.set('totalPages', totalPages));
            },
        };
    }
}

const instance = new ReservationStore({ storageKey: 'reservations' });

export default instance;
